import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import { setTenantLogo } from '../config/documentStyles';
import { updateWhiteLabelFromTenant } from '../lib/whitelabel';
import { bloqueActivo } from '../config/modules';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface TenantConfig {
  id: string;
  nombre: string;
  slug: string;
  nombreMarca: string | null;
  logoUrl: string | null;
  logoHeaderUrl: string | null;
  faviconUrl: string | null;
  colorPrimario: string;
  colorSecundario: string;
  colorAcento: string;
  fuente: string;
  idiomaPredeterminado: string;
  plan: string;
  modulosActivos: string[];
}

interface TenantContextValue {
  tenant: TenantConfig | null;
  loading: boolean;
  error: string | null;
  tieneModulo: (code: string) => boolean;
  tieneBloque: (bloque: string) => boolean;
}

// ─── Default values ─────────────────────────────────────────────────────────
// Los modulos se leen SIEMPRE desde BD via /api/v1/config/tenant.
// DEFAULT_TENANT solo se usa como fallback visual mientras carga o si la BD
// no responde. modulosActivos vacio → el usuario vera un estado de error claro
// en lugar de una app fantasma con modulos inventados.

const DEFAULT_TENANT: TenantConfig = {
  id: '',
  nombre: 'V·IANet Governance',
  slug: 'valoria',
  nombreMarca: 'VALOR·IA Governance',
  logoUrl: null,
  logoHeaderUrl: null,
  faviconUrl: null,
  colorPrimario: '#1F3864',
  colorSecundario: '#2F5496',
  colorAcento: '#33BCE1',
  fuente: 'Montserrat',
  idiomaPredeterminado: 'es',
  plan: 'BASIC',
  modulosActivos: [],
};

// ─── Context ────────────────────────────────────────────────────────────────

const TenantContext = createContext<TenantContextValue>({
  tenant: DEFAULT_TENANT,
  loading: false,
  error: null,
  tieneModulo: () => false,
  tieneBloque: () => false,
});

export function useTenant(): TenantContextValue {
  return useContext(TenantContext);
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function applyTenantCSS(config: TenantConfig) {
  const root = document.documentElement;
  root.style.setProperty('--color-primario', config.colorPrimario);
  root.style.setProperty('--color-secundario', config.colorSecundario);
  root.style.setProperty('--color-acento', config.colorAcento);
  // Also set wl- variables for backward compatibility
  root.style.setProperty('--wl-primary', config.colorSecundario);
  root.style.setProperty('--wl-primary-dark', '#16365B');
  root.style.setProperty('--wl-primary-darker', config.colorPrimario);
  root.style.setProperty('--wl-accent', config.colorAcento);
}

function loadFont(fontName: string) {
  if (fontName === 'Montserrat' || !fontName) return;
  const existingLink = document.querySelector(`link[data-tenant-font="${fontName}"]`);
  if (existingLink) return;

  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontName)}:wght@300;400;500;600;700&display=swap`;
  link.dataset.tenantFont = fontName;
  document.head.appendChild(link);
}

function updateFavicon(url: string | null) {
  if (!url) return;
  // 0589 fix #4: borrar TODOS los links icon previos (incluido el index.html
  // con type="image/svg+xml" que no matchea PNG/JPG del tenant) y crear uno nuevo
  // con el type derivado de la extension del archivo del tenant.
  document.querySelectorAll("link[rel~='icon']").forEach((el) => el.remove());
  const link = document.createElement('link');
  link.rel = 'icon';
  const ext = url.split('?')[0]?.split('.').pop()?.toLowerCase() ?? '';
  if (ext === 'svg') link.type = 'image/svg+xml';
  else if (ext === 'png') link.type = 'image/png';
  else if (ext === 'jpg' || ext === 'jpeg') link.type = 'image/jpeg';
  else if (ext === 'ico') link.type = 'image/x-icon';
  link.href = url;
  document.head.appendChild(link);
}

// ─── Provider ───────────────────────────────────────────────────────────────

interface TenantProviderProps {
  children: ReactNode;
}

export function TenantProvider({ children }: TenantProviderProps) {
  const [state, setState] = useState<{
    tenant: TenantConfig | null;
    loading: boolean;
    error: string | null;
  }>({
    tenant: DEFAULT_TENANT,
    loading: true,
    error: null,
  });

  useEffect(() => {
    const slug = import.meta.env.VITE_TENANT_SLUG ?? 'valoria';
    const apiBase = import.meta.env.VITE_API_BASE ?? '';

    fetch(`${apiBase}/api/v1/config/tenant?slug=${encodeURIComponent(slug)}`)
      .then(async (res) => {
        if (!res.ok) {
          throw new Error(`Error ${res.status} al cargar configuración del tenant`);
        }
        const json = await res.json();
        return json.data as TenantConfig;
      })
      .then((tenant) => {
        applyTenantCSS(tenant);
        if (tenant.fuente && tenant.fuente !== 'Montserrat') loadFont(tenant.fuente);
        updateFavicon(tenant.faviconUrl);
        // 0589 fix #4: titulo de pestana del navegador con nombreMarca del tenant
        // Pattern white-label: "Banco X · Governance" en lugar de "V·IAnet" generico.
        const marca = tenant.nombreMarca || tenant.nombre || 'V·IAnet';
        document.title = marca === 'V·IAnet' ? 'V·IAnet' : `${marca} · Governance`;
        setTenantLogo(tenant.logoUrl);
        updateWhiteLabelFromTenant(tenant);
        setState({ tenant, loading: false, error: null });
      })
      .catch((err) => {
        console.warn('[TenantProvider] Error cargando tenant, usando defaults:', err.message);
        applyTenantCSS(DEFAULT_TENANT);
        setState({ tenant: DEFAULT_TENANT, loading: false, error: err.message });
      });
  }, []);

  const modulosActivos = useMemo(() => state.tenant?.modulosActivos ?? [], [state.tenant]);

  const tieneModulo = useCallback(
    (code: string) => modulosActivos.includes(code),
    [modulosActivos],
  );

  const tieneBloque = useCallback(
    (bloque: string) => bloqueActivo(bloque, modulosActivos),
    [modulosActivos],
  );

  const value = useMemo<TenantContextValue>(
    () => ({
      tenant: state.tenant,
      loading: state.loading,
      error: state.error,
      tieneModulo,
      tieneBloque,
    }),
    [state, tieneModulo, tieneBloque],
  );

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}
